import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Box } from '@tink/core/src/components/atoms/Box/Box';
import { ApplicationUnverifiedLabel } from '../../molecules/ApplicationUnverifiedLabel/ApplicationUnverifiedLabel';
import { StyledLock } from '../../atoms/Lock/Lock';
import { Stack } from '../../atoms/Stack/Stack';
import { PrimaryButton } from '../../atoms/Button/Button';
import { ScreenWrapper } from '../../atoms/ScreenWrapper/ScreenWrapper';
import { Provider, ProviderEntity } from '../../../models/provider';
import { useAnalytics } from '../../../core/Analytics/AnalyticsProvider';
import { PageView } from '../../../models/analytics';
import { ContentBody } from '../../atoms/ContentBody/ContentBody';
import { Footer } from '../../atoms/Footer/Footer';
import { BackButton } from '../../organisms/Header/HeaderMolecule';
import {
  getInstructions,
  getInstructionsTitle,
} from './organisms/AwaitingThirdpartyAuthenticationContainer/AuthAwaitingThirdpartyAuthenticationContainer/thirdparty-instructions';
import {
  SecurityCaption,
  SecurityText,
} from './organisms/AwaitingThirdpartyAuthenticationContainer/AwaitingThirdpartyAuthenticationContainer.styles';
import { messages as sharedMessages } from './organisms/AwaitingThirdpartyAuthenticationContainer/messages';
import { messages as awaitingThirdPartyMessages } from './organisms/AwaitingThirdpartyAuthenticationContainer/molecules/AwaitingThirdpartyAuthorization/messages';
import { InstructionListHeader } from './organisms/AwaitingThirdpartyAuthenticationContainer/molecules/InstructionList/InstructionListHeader';
import { InstructionListBody } from './organisms/AwaitingThirdpartyAuthenticationContainer/molecules/InstructionList/InstructionListBody';
import { AwaitingScreenHeader } from './organisms/molecules/AwaitingScreenHeader/AwaitingScreenHeader';
import { InfoContainer } from './organisms/AwaitingThirdpartyAuthenticationContainer/AuthAwaitingThirdpartyAuthenticationContainer/AuthAwaitingThirdpartyAuthenticationContainer.styles';
import { messages } from './organisms/AwaitingThirdpartyAuthenticationContainer/AuthAwaitingThirdpartyAuthenticationContainer/messages';

export type AwaitingTppInstructionScreenProps = {
  provider: ProviderEntity;
  clientName: string;
  onClick: () => void;
  isVerified: boolean;
} & BackButton;

export const AwaitingTppInstructionScreen: React.FC<AwaitingTppInstructionScreenProps> = ({
  provider,
  clientName,
  onBack,
  onClick,
  isVerified,
}) => {
  const { tinklinkAnalytics } = useAnalytics();

  useEffect(() => {
    tinklinkAnalytics.sendViewEvent({ view: PageView.AWAITING_THIRD_PARTY_AUTHENTICATION_SCREEN });
  }, [tinklinkAnalytics]);

  const instructions = getInstructions({ providerName: provider.displayName, clientName });

  const instructionsTitle = getInstructionsTitle({ providerName: provider.displayName });

  const body = Provider.isFineco(provider) ? (
    <InfoContainer>
      <FormattedMessage
        {...messages.consentInformation}
        values={{ providerName: provider.displayName }}
      />
    </InfoContainer>
  ) : (
    <SecurityCaption>
      <StyledLock />
      <SecurityText>
        <FormattedMessage {...sharedMessages.securityCaption} />
      </SecurityText>
    </SecurityCaption>
  );

  return (
    <ScreenWrapper>
      <AwaitingScreenHeader
        allowExit={Boolean(onBack)}
        onBack={onBack}
        pageView={PageView.AWAITING_THIRD_PARTY_AUTHENTICATION_SCREEN}
      />
      <ContentBody>
        <Stack justifyContent="center">
          <InstructionListHeader title={instructionsTitle} provider={provider} />
          <InstructionListBody instructions={instructions} />
          <Box my="medium">{body}</Box>

          {!isVerified && (
            <Box my="medium">
              <ApplicationUnverifiedLabel isVerified={false} />
            </Box>
          )}
        </Stack>
        <Footer stickToBottom>
          <PrimaryButton
            pageView={PageView.AWAITING_THIRD_PARTY_AUTHENTICATION_SCREEN}
            variant="contained"
            color="primary"
            onClick={onClick}
          >
            <FormattedMessage
              {...awaitingThirdPartyMessages.buttonText}
              values={{ providerName: provider.displayName }}
            />
          </PrimaryButton>
        </Footer>
      </ContentBody>
    </ScreenWrapper>
  );
};
